import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback } from "react";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import { FaIcon } from "@shared/client/components/fa-icon";
import { showToast } from "@shared/client/components/toast/show-toast";
import { useTextRangeRects } from "../use-text-range-rects";
import { userAnnotationStore } from "@/data/user-annotation-store";
// Pixel offset of the floating delete pill above the first rect's
// top edge. Picked to land the pill just clear of the highlighted
// text — close enough that the pointer can travel from the rect
// to the pill without a visible "gap miss", but not so close that
// the pill clips the descenders of an upper line of text. Lives
// here (not in SCSS) because the pill's `top` is computed
// inline against the per-line rect's CSS coords; SCSS can't see
// the per-rect math.
const DELETE_PILL_OFFSET_PX = 28;
export const TextHighlightAnnotationView = memo(function TextHighlightAnnotationView({ annotation, mode, containerRef, selection, }) {
    const { color } = annotation.content;
    // Phase 2: range lives on `anchor`, not on `content`. Without a
    // `text_range` anchor there's nothing to paint.
    const range = annotation.anchor?.type === "text_range" ? annotation.anchor : undefined;
    const rects = useTextRangeRects({
        containerRef,
        mode,
        start: range?.start,
        end: range?.end,
    });
    const isSelectable = selection !== undefined;
    const isSelected = selection?.isSelected === true;
    const handleRectPointerDown = useCallback((e) => {
        if (!selection)
            return;
        // Mouse-only filter: secondary-button clicks shouldn't steal
        // selection (right-click context menu, middle-click paste).
        if (e.pointerType === "mouse" && e.button !== 0)
            return;
        // Stop bubbling so the modal's outside-click deselect (in
        // `useAnnotationSelection`) doesn't immediately fire after
        // we set the selection — same defensive pattern the comment
        // popover uses on its rect taps.
        e.stopPropagation();
        selection.onSelect(annotation.id);
    }, [selection, annotation.id]);
    const handleDelete = useCallback((e) => {
        // Block bubbling so the click doesn't also re-select us via
        // the rect handler beneath the pill (the pill paints over
        // the first rect in absolute coords).
        e.stopPropagation();
        void userAnnotationStore
            .remove(annotation.id)
            .catch(() => {
            showToast("Failed to delete highlight");
        });
    }, [annotation.id]);
    if (rects.length === 0)
        return null;
    // Inline-style fallback to the CSS variable when no `color` is
    // persisted (defensive — the schema requires `color`, but a
    // legacy row might lack it).
    const tint = color || undefined;
    const wrapperClassName = [
        "annotation-text-highlight",
        `annotation-text-highlight--mode-${mode}`,
        isSelectable && "annotation-text-highlight--selectable",
        isSelected && "annotation-text-highlight--selected",
    ]
        .filter(Boolean)
        .join(" ");
    const rectClassName = [
        "annotation-text-highlight__rect",
        isSelectable && "annotation-text-highlight__rect--selectable",
        isSelected && "annotation-text-highlight__rect--selected",
    ]
        .filter(Boolean)
        .join(" ");
    // Anchor the floating delete pill above the first rect's top-
    // left. Using the first rect (rather than the union bbox) keeps
    // the pill's position deterministic across multi-line wraps —
    // the delete affordance always lands at the start of the
    // highlighted span, not at some computed centroid that would
    // hop around as text reflows.
    const pillRect = rects[0];
    // Rects come back in container-VISUAL pixels. The inline layer
    // is untransformed (`inset: 0` of the host), so its CSS pixel
    // grid equals the visual grid one-to-one — no scale
    // compensation needed. (Compare with the transformed
    // `.annotations-container`, where this view used to live and
    // where the math required a divide-by-`--annotation-natural-
    // scale` to undo the container's `transform: scale(...)`.)
    return (_jsxs("div", { className: wrapperClassName, "aria-hidden": !isSelected, "data-annotation-id": annotation.id, children: [rects.map((r, i) => (_jsx("div", { className: rectClassName, onPointerDown: isSelectable ? handleRectPointerDown : undefined, style: {
                    top: `${r.top}px`,
                    left: `${r.left}px`,
                    width: `${r.width}px`,
                    height: `${r.height}px`,
                    ...(tint ? { background: tint } : {}),
                } }, i))), isSelected && pillRect ? (_jsx("button", { type: "button", className: "annotation-text-highlight__delete", 
                // Anchor the pill ABOVE the first rect — `top` is the
                // rect's top minus a small visual gap. SCSS clamps the
                // pill against the layer's `overflow: visible` bounds
                // so a near-top highlight (e.g. first line of the
                // panel) doesn't paint the pill outside the panel.
                style: {
                    top: `${pillRect.top - DELETE_PILL_OFFSET_PX}px`,
                    left: `${pillRect.left}px`,
                }, onClick: handleDelete, "aria-label": "Delete highlight", title: "Delete highlight", children: _jsx(FaIcon, { icon: faTrashCan, className: "annotation-text-highlight__delete-icon" }) })) : undefined] }));
});
